import { adApiRequest } from './banner-kit-api';

/**
 * What the brand's home page shows above its first list of sections, as spark-creatives reads it from the site's own
 * config (`GET /creatives/tenant/{tenant_id}/above-the-fold?domain=`): the Casino / Sports category cards (the brand's
 * own general resource) and the parts every brand on the template shares (the casino navigation, an image fixed in the
 * template). The answer also names the hero carousel's banners; those are the brand's pool banners, edited under All
 * Promotions & Creatives, so they are not read here. `domain` is the site's PREVIEW host, as Game Lists
 * sends it: the service reads the site's page to learn its id, and off production the public domain is often not
 * connected (it answers 400).
 */

export interface AtfCards {
  recordId: string;
  /** The record's images by name (`casino_square_key_center`, ...). */
  images: Record<string, string>;
  /** The texts the cards show, in English (else the first language the record has). */
  title: string;
  casino: string;
  sport: string;
  editable: boolean;
  canRevert: boolean;
}

export interface AtfNavLink {
  id: string;
  name: string;
  /** The icon the site's navigation shows for the link (empty when the record has none). */
  icon: string;
}

/** A part every brand on the template shares. Read-only. */
export type AtfShared =
  | { kind: 'image'; key: string; name: string; url: string; alt: string }
  | { kind: 'navigation'; key: string; name: string; links: AtfNavLink[] }
  | { kind: 'data'; key: string; name: string };

export interface AboveTheFold {
  cards: AtfCards | null;
  shared: AtfShared[];
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const str = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');
const strMap = (value: unknown): Record<string, string> => {
  const out: Record<string, string> = {};
  if (!isRecord(value)) return out;
  for (const [k, v] of Object.entries(value)) if (str(v)) out[k] = str(v);
  return out;
};

/** The icon a navigation link shows: its default nav icon, else any icon it has. */
const linkIcon = (icons: Record<string, string>): string =>
  icons.nav_icon_ar11_default || icons.sidemenu_icon_ar11_default || Object.values(icons)[0] || '';

const normaliseShared = (raw: unknown, index: number): AtfShared | null => {
  if (!isRecord(raw)) return null;
  const key = `${str(raw.component_id)}:${str(raw.node_id)}:${index}`;
  const name = str(raw.component_name) || str(raw.source_id);
  if (raw.kind === 'image') {
    const url = str(raw.url);
    return url ? { kind: 'image', key, name, url, alt: str(raw.alt) } : null;
  }
  if (raw.kind === 'navigation') {
    const links = (Array.isArray(raw.links) ? raw.links : []).filter(isRecord).map((link) => ({
      id: str(link.id),
      name: str(link.name),
      icon: linkIcon(strMap(link.icons)),
    }));
    return { kind: 'navigation', key, name, links };
  }
  return { kind: 'data', key, name };
};

/** The above-the-fold answer from its wire shape (bare or wrapped in `data`). `cards` is absent on the wire when the
 *  home page has none; cards with no record or no images read as none. Pure. */
export const normaliseAboveTheFold = (raw: unknown): AboveTheFold => {
  const outer = isRecord(raw) ? raw : {};
  const d = isRecord(outer.data) ? outer.data : outer;

  const c = isRecord(d.cards) ? d.cards : null;
  const images = strMap(c?.images);
  const titles = isRecord(c?.titles) ? c.titles : {};
  const text = isRecord(titles.en) ? titles.en : (Object.values(titles).find(isRecord) ?? {});
  const cards =
    c && str(c.record_id) && Object.keys(images).length
      ? {
          recordId: str(c.record_id),
          images,
          title: str(text.title),
          casino: str(text.casino),
          sport: str(text.sport),
          editable: c.editable === true,
          canRevert: c.can_revert === true,
        }
      : null;

  const shared = (Array.isArray(d.shared) ? d.shared : [])
    .map(normaliseShared)
    .filter((part): part is AtfShared => part !== null);

  return { cards, shared };
};

/** Whether there is anything to show above the fold here (the brand's cards or a shared part). Pure. */
export const hasAboveTheFold = (atf: AboveTheFold): boolean => Boolean(atf.cards || atf.shared.length);

/** The cards' images as the owner sees them: each card's square art then its cut-out, Casino first, then any image
 *  the record has under another name. `card` names which card it belongs to, `cutout` marks a transparent one. Pure. */
export const cardTiles = (
  cards: AtfCards,
): { name: string; url: string; card: 'casino' | 'sport' | 'other'; cutout: boolean }[] => {
  const known = [
    'casino_square_key_center',
    'casino_square_key_center_transparent_21',
    'sport_square_key_center',
    'sport_square_key_center_transparent_21',
  ];
  const names = [
    ...known.filter((name) => cards.images[name]),
    ...Object.keys(cards.images)
      .filter((name) => !known.includes(name))
      .sort(),
  ];
  return names.map((name) => ({
    name,
    url: cards.images[name],
    card: name.startsWith('casino_') ? 'casino' : name.startsWith('sport_') ? 'sport' : 'other',
    cutout: name.includes('transparent'),
  }));
};

export type AboveTheFoldRead = { ok: true; value: AboveTheFold } | { ok: false; aborted?: boolean };

/**
 * A read's answer as the tab takes it. A 4xx is a fixed answer a retry cannot change (a backend without the route
 * yet answers 404, a site it cannot read 400, a brand it will not serve 403), so it reads as nothing above the fold
 * and the tab stays away instead of standing on an error whose Try again never works. A 5xx, a network failure or an
 * answer that is not JSON is a failure worth retrying. Pure.
 */
export const aboveTheFoldReadOf = (res: Awaited<ReturnType<typeof adApiRequest<Record<string, unknown>>>>): AboveTheFoldRead => {
  if (res.ok) return { ok: true, value: normaliseAboveTheFold(res.data) };
  if (res.status >= 400 && res.status < 500) return { ok: true, value: { cards: null, shared: [] } };
  return { ok: false, aborted: res.aborted };
};

/** Read the brand's above the fold. Never throws. */
export const fetchAboveTheFold = async (tenantId: number, domain: string, signal?: AbortSignal): Promise<AboveTheFoldRead> =>
  aboveTheFoldReadOf(
    await adApiRequest<Record<string, unknown>>(
      'get',
      `/creatives/tenant/${encodeURIComponent(String(tenantId))}/above-the-fold?domain=${encodeURIComponent(domain)}`,
      undefined,
      signal,
    ),
  );

/** The cards regeneration's job kind and placement, as spark-creatives queues it (`pkg/jobs` AtfCardsKind). */
export const ATF_CARDS_KIND = 'atf_cards';
export const ATF_CARDS_PLACEMENT = 'above_the_fold';
/** Images one regeneration draws (a square and a cut-out per card); it is charged the per-image price for each. */
export const ATF_CARDS_DRAWS = 4;

/** A queued regeneration (`jobId`), or why not: 402 with the credits `needed`, else the service's status and message. */
export type CardsRegenerateResult =
  | { ok: true; jobId: string; status: string }
  | { ok: false; status: number; needed?: number; error?: string };

/** The queue answer as a result: a 202 must name its job, or the run cannot be followed. Pure. */
export const cardsRegenerateResult = (res: Awaited<ReturnType<typeof adApiRequest<Record<string, unknown>>>>): CardsRegenerateResult => {
  if (!res.ok) return { ok: false, status: res.status, needed: res.needed, error: res.error };
  const jobId = str(res.data.job_id);
  if (!jobId) return { ok: false, status: 0, error: 'the regeneration was not queued' };
  return { ok: true, jobId, status: str(res.data.status) || 'queued' };
};

/**
 * Queue a regeneration of the brand's Casino and Sports cards in its art style (a paid job, refunded when it fails).
 * `recordId` is the cards record the page read: the service refuses one that is not the brand's own. Never throws.
 */
export const regenerateCards = async (tenantId: number, domain: string, recordId: string): Promise<CardsRegenerateResult> =>
  cardsRegenerateResult(
    await adApiRequest<Record<string, unknown>>(
      'post',
      `/creatives/tenant/${encodeURIComponent(String(tenantId))}/above-the-fold/cards/regenerate?domain=${encodeURIComponent(domain)}`,
      { record_id: recordId },
    ),
  );

/* ---------- per-image redraw of the cards (spark-creatives /site-art, over casino-creation's key-art previews) ---------- */

/** One image occurrence of a casino-creation deployment, in the style the site shows it in. */
export interface SiteArtAsset {
  /** casino-creation's id of this exact occurrence (it may change after an apply). */
  assetId: string;
  /** The image's name in the published record: for the cards, the same name the cards record gives the image. */
  fieldName: string;
  /** casino-creation can draw this image again. */
  regenerable: boolean;
  /** casino-creation's reason it cannot ("" when it can). */
  unsupportedReason: string;
  /** Regenerable, and the style the site shows it in is known: a redraw is accepted. */
  redrawable: boolean;
  /** Tagged with the style the site shows (false for an untagged occurrence). */
  styleVerified: boolean;
  /** The image this occurrence holds now. */
  currentUrl: string;
}

/** One of the brand's successful casino-creation deployments and the images the site shows of it. */
export interface SiteArtDeployment {
  deploymentUuid: string;
  deploymentType: string;
  /** The deployment's redraw not yet applied or discarded ("" when none); a new one is refused until it is. */
  pendingRedrawId: string;
  assets: SiteArtAsset[];
}

/** The brand's website art the site shows (`GET /creatives/tenant/{t}/site-art/assets`), with what a redraw costs. */
export interface SiteArt {
  look: string;
  styleId: string;
  /** Credits one redraw costs (0 when not priced). */
  redrawPrice: number;
  /** This environment has a price for a redraw; without one every redraw is refused. */
  redrawPriced: boolean;
  deployments: SiteArtDeployment[];
}

const num = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);
const records = (value: unknown): Record<string, unknown>[] => (Array.isArray(value) ? value.filter(isRecord) : []);

/** The site-art answer from its wire shape (bare or wrapped in `data`); every list an array. Pure. */
export const normaliseSiteArt = (raw: unknown): SiteArt => {
  const outer = isRecord(raw) ? raw : {};
  const d = isRecord(outer.data) ? outer.data : outer;
  return {
    look: str(d.look),
    styleId: str(d.style_id),
    redrawPrice: num(d.redraw_price),
    redrawPriced: d.redraw_priced === true,
    deployments: records(d.deployments).map((dep) => ({
      deploymentUuid: str(dep.deployment_uuid),
      deploymentType: str(dep.deployment_type),
      pendingRedrawId: str(dep.pending_redraw_id),
      assets: records(dep.assets).map((a) => ({
        assetId: str(a.asset_id),
        fieldName: str(a.field_name),
        regenerable: a.regenerable === true,
        unsupportedReason: str(a.unsupported_reason),
        redrawable: a.redrawable === true,
        styleVerified: a.style_verified === true,
        currentUrl: str(a.current_url),
      })),
    })),
  };
};

/** casino-creation's deployment type whose published record is the brand's category cards. */
export const ATF_HERO_TYPE = 'above_the_fold_hero';

/**
 * The brand's hero deployment: the one the cards the site shows come from, as spark-creatives' "Regenerate both cards"
 * picks it. casino-creation lists the newest first, but an older hero becomes the newest when a redraw is applied on
 * it, so with several heroes the one carrying the most of the live card images (`live`: the cards record's images by
 * name) is taken, the first listed on a tie; the first listed when none carries any, or without `live`. Null when the
 * brand has none. Pure.
 */
export const atfHeroOf = (art: SiteArt, live?: Record<string, string>): SiteArtDeployment | null => {
  const heroes = art.deployments.filter((dep) => dep.deploymentType === ATF_HERO_TYPE && dep.deploymentUuid);
  const carried = (hero: SiteArtDeployment): number =>
    Object.entries(live ?? {}).filter(
      ([name, url]) => url && hero.assets.some((a) => a.fieldName === name && a.currentUrl === url),
    ).length;
  let pick: SiteArtDeployment | null = heroes[0] ?? null;
  let most = 0;
  for (const hero of heroes) {
    const n = carried(hero);
    if (n > most) [pick, most] = [hero, n];
  }
  return pick;
};

/** What a card image offers: a redraw of the occurrence `assetId`, or why not (`reason` is casino-creation's, and ""
 *  when the image can be drawn again but the style the site shows it in cannot be told). */
export type CardRedrawOffer = { kind: 'redrawable'; assetId: string } | { kind: 'blocked'; reason: string };

/**
 * The hero's images by name (the card tiles' names), each with what it offers. Two occurrences under one name (one
 * untagged) resolve to the one a redraw is accepted for, the one tagged with the site's style first. Pure.
 */
export const cardRedrawOffers = (hero: SiteArtDeployment): Record<string, CardRedrawOffer> => {
  const score = (a: SiteArtAsset): number => (a.redrawable ? 2 : 0) + (a.styleVerified ? 1 : 0);
  const best: Record<string, SiteArtAsset> = {};
  for (const a of hero.assets) {
    if (!a.fieldName || !a.assetId) continue;
    const seen = best[a.fieldName];
    if (!seen || score(a) > score(seen)) best[a.fieldName] = a;
  }
  const offers: Record<string, CardRedrawOffer> = {};
  for (const [name, a] of Object.entries(best)) {
    offers[name] = a.redrawable
      ? { kind: 'redrawable', assetId: a.assetId }
      : { kind: 'blocked', reason: a.regenerable ? '' : a.unsupportedReason };
  }
  return offers;
};

export type SiteArtRead = { ok: true; value: SiteArt } | { ok: false; aborted?: boolean };

/** A site-art read as the tab takes it: any refusal or failure means no redraw is offered (the cards still show). Pure. */
export const siteArtReadOf = (res: Awaited<ReturnType<typeof adApiRequest<Record<string, unknown>>>>): SiteArtRead =>
  res.ok ? { ok: true, value: normaliseSiteArt(res.data) } : { ok: false, aborted: res.aborted };

/** Read the brand's website art the site shows (it reads every deployment from casino-creation: read it only where it
 *  is used). Never throws. */
export const fetchSiteArt = async (tenantId: number, signal?: AbortSignal): Promise<SiteArtRead> =>
  siteArtReadOf(
    await adApiRequest<Record<string, unknown>>('get', `/creatives/tenant/${encodeURIComponent(String(tenantId))}/site-art/assets`, undefined, signal),
  );

/** drawing (charged, being drawn) | ready (apply or discard it before expiresAt) | applied | discarded | failed |
 *  stale | expired. */
export type SiteRedrawStatus = 'drawing' | 'ready' | 'applied' | 'discarded' | 'failed' | 'stale' | 'expired' | string;

/** One per-image redraw: one image of one deployment and the candidate casino-creation draws for it. */
export interface SiteRedraw {
  id: string;
  status: SiteRedrawStatus;
  /** The site_redraw job that follows the draw (creative_job events, parent_id = the redraw). */
  jobId: string;
  deploymentUuid: string;
  assetId: string;
  fieldName: string;
  originalUrl: string;
  /** The drawn candidate ("" until ready). */
  candidateUrl: string;
  refunded: boolean;
  /** The code it ended with ("" otherwise). */
  error: string;
  /** RFC 3339, casino-creation's apply deadline ("" until known). */
  expiresAt: string;
}

/** The job kind spark-creatives follows a redraw's draw with (`pkg/jobs` SiteRedrawKind) and its placement. */
export const SITE_REDRAW_KIND = 'site_redraw';
export const SITE_REDRAW_PLACEMENT = 'website';

/** Whether a redraw still holds its deployment (drawn or being drawn, neither applied nor thrown away). Pure. */
export const isPendingRedraw = (r: SiteRedraw): boolean => r.status === 'drawing' || r.status === 'ready';

/** A redraw call's answer: the redraw as recorded, or the refusal (its bare code, and the credits needed on a 402). */
export type SiteRedrawCall = { ok: true; redraw: SiteRedraw } | { ok: false; status: number; error?: string; needed?: number };

/** A redraw call's answer from its wire shape: a 2xx must carry the redraw's id. Pure. */
export const siteRedrawCallOf = (res: Awaited<ReturnType<typeof adApiRequest<Record<string, unknown>>>>): SiteRedrawCall => {
  if (!res.ok) return { ok: false, status: res.status, error: res.error, needed: res.needed };
  const d = res.data;
  const id = str(d.id);
  if (!id) return { ok: false, status: 0, error: 'the answer named no redraw' };
  return {
    ok: true,
    redraw: {
      id,
      status: str(d.status),
      jobId: str(d.job_id),
      deploymentUuid: str(d.deployment_uuid),
      assetId: str(d.asset_id),
      fieldName: str(d.field_name),
      originalUrl: str(d.original_url),
      candidateUrl: str(d.candidate_url),
      refunded: d.refunded === true,
      error: str(d.error),
      expiresAt: str(d.expires_at),
    },
  };
};

const siteArtPath = (tenantId: number): string => `/creatives/tenant/${encodeURIComponent(String(tenantId))}/site-art`;

/** Charge and queue a redraw of one image (202: drawing). Never throws. */
export const requestSiteRedraw = async (tenantId: number, deploymentUuid: string, assetId: string): Promise<SiteRedrawCall> =>
  siteRedrawCallOf(
    await adApiRequest<Record<string, unknown>>(
      'post',
      `${siteArtPath(tenantId)}/assets/${encodeURIComponent(deploymentUuid)}/${encodeURIComponent(assetId)}/redraw`,
    ),
  );

/** Read one redraw as recorded. Never throws. */
export const fetchSiteRedraw = async (tenantId: number, id: string, signal?: AbortSignal): Promise<SiteRedrawCall> =>
  siteRedrawCallOf(await adApiRequest<Record<string, unknown>>('get', `${siteArtPath(tenantId)}/redraws/${encodeURIComponent(id)}`, undefined, signal));

/** Put a ready candidate live in place of the image. Never throws. */
export const applySiteRedraw = async (tenantId: number, id: string): Promise<SiteRedrawCall> =>
  siteRedrawCallOf(await adApiRequest<Record<string, unknown>>('post', `${siteArtPath(tenantId)}/redraws/${encodeURIComponent(id)}/apply`));

/** Throw a candidate away (not refunded). Never throws. */
export const discardSiteRedraw = async (tenantId: number, id: string): Promise<SiteRedrawCall> =>
  siteRedrawCallOf(await adApiRequest<Record<string, unknown>>('delete', `${siteArtPath(tenantId)}/redraws/${encodeURIComponent(id)}`));

const DAY_MS = 24 * 60 * 60 * 1000;
/** casino-creation keeps an un-applied candidate this many days. */
export const REDRAW_KEEP_DAYS = 7;

/** Whole days left to apply a candidate (at least 1), or the full window when the deadline is not known. Pure. */
export const redrawDaysLeft = (expiresAt: string, now: number): number => {
  const at = Date.parse(expiresAt);
  if (!expiresAt || Number.isNaN(at)) return REDRAW_KEEP_DAYS;
  return Math.max(1, Math.ceil((at - now) / DAY_MS));
};

/** Why a redraw request was refused, as the tab answers it. Pure. */
export type RedrawRefusal = 'credits' | 'pending' | 'site-busy' | 'not-redrawable' | 'refunded' | 'unavailable' | 'busy' | 'failed';

export const redrawRefusal = (status: number, error?: string): RedrawRefusal => {
  if (status === 402) return 'credits';
  if (status === 404) return 'not-redrawable';
  switch (error) {
    case 'site_redraw_pending':
      return 'pending';
    case 'site_regen_running':
      return 'site-busy';
    case 'site_redraw_not_displayed':
    case 'site_redraw_not_regenerable':
    case 'site_redraw_asset_not_found':
      return 'not-redrawable';
    case 'site_redraw_wrong_style':
    case 'site_redraw_failed':
      return 'refunded';
    case 'site_redraw_not_priced':
    case 'site_redraw_unavailable':
      return 'unavailable';
    case 'site_redraw_busy':
      return 'busy';
    default:
      return 'failed';
  }
};

/** Why an apply was refused. `gone`: the redraw no longer exists or is not ready (read the art again); `apply-failed`:
 *  read the redraw again to learn whether its candidate is kept. Pure. */
export type ApplyRefusal = 'stale' | 'expired' | 'busy' | 'gone' | 'apply-failed' | 'unavailable' | 'failed';

export const applyRefusal = (status: number, error?: string): ApplyRefusal => {
  if (status === 404) return 'gone';
  switch (error) {
    case 'site_redraw_stale':
      return 'stale';
    case 'site_redraw_expired':
      return 'expired';
    case 'site_redraw_busy':
      return 'busy';
    case 'site_redraw_not_ready':
      return 'gone';
    case 'site_redraw_apply_failed':
      return 'apply-failed';
    case 'site_redraw_unavailable':
      return 'unavailable';
    default:
      return 'failed';
  }
};

/** Why a discard was refused. Pure. */
export type DiscardRefusal = 'applied' | 'busy' | 'gone' | 'failed';

export const discardRefusal = (status: number, error?: string): DiscardRefusal => {
  if (status === 404) return 'gone';
  if (error === 'site_redraw_applied') return 'applied';
  if (error === 'site_redraw_busy') return 'busy';
  return 'failed';
};
